// Load a statement: pick the file, watch it be read, see whether the reading tied
// to the document itself, and only then load it.
//
// THE RECONCILIATION PANEL IS THE CONTROL, NOT DECORATION. A statement is around
// 350 lines. The CSV path beside this one shows six preview rows and asks a
// person to agree, which was the right shape when nobody had seen the export and
// is not a control over 350 lines. A PDF statement states its own opening
// balance, closing balance, running balance per line, and turnover totals, so the
// question this panel answers is not "does this look right to you" but "did our
// reading of it agree with the document's own arithmetic". A person can check
// that in two seconds and could not check 350 lines in an afternoon.
//
// There is no edit affordance anywhere in here on purpose. A reading that did not
// tie cannot be corrected by hand: a person fixing three lines of a statement
// that failed is how a wrong figure acquires a human signature.
import { useRef, useState } from 'react'
import { Upload, AlertCircle, CheckCircle2, X, FileText } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { zar } from './cashModel'

const MAX_BYTES = 15 * 1024 * 1024

export type StatementAccount = { account_key: string; display_name: string }

export type Reconciliation = {
  balance_chain: boolean
  credit_turnover: boolean | null
  debit_turnover: boolean | null
  reconciled: boolean
  read_credit_count: number
  read_credit_zar: number
  read_debit_count: number
  read_debit_zar: number
  closing_from_chain_zar: number | null
  note: string
}

export type Reading = {
  account_number: string | null
  statement_number: string | null
  period_from: string | null
  period_to: string | null
  opening_balance_zar: number | null
  closing_balance_zar: number | null
  stated_credit_count: number | null
  stated_credit_zar: number | null
  stated_debit_count: number | null
  stated_debit_zar: number | null
  reconciliation: Reconciliation
  unread: string[]
  warnings: string[]
}

export type Comparison = {
  ran: boolean
  matched_same_day: number
  matched_in_window: number
  matched_total: number
  proposals_waiting: number
  credits_attributed: number
  credits_left_ambiguous: number
  credits_left_unnamed: number
  error: string | null
  note: string
  credits_note: string | null
}

export type UploadOutcome = {
  ok: boolean
  loaded: boolean
  comparison?: Comparison | null
  reading?: Reading | null
  line_sample?: Array<Record<string, unknown>>
  result?: Record<string, unknown>
  account_warning?: string | null
  error?: string
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

function Check({ ok, label, detail }: { ok: boolean | null; label: string; detail: string }) {
  return (
    <div className="flex items-start gap-2">
      {ok === true ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      ) : ok === false ? (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
      ) : (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <div>
        <div className="text-xs font-medium text-foreground">{label}</div>
        <div className="text-[11px] text-muted-foreground">{detail}</div>
      </div>
    </div>
  )
}

export function StatementUpload({
  accounts,
  busy,
  outcome,
  error,
  onCancel,
  onSubmit,
}: {
  accounts: StatementAccount[]
  busy: boolean
  outcome: UploadOutcome | null
  error: string | null
  onCancel: () => void
  onSubmit: (args: { accountKey: string; file: File; contentBase64: string }) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [accountKey, setAccountKey] = useState(accounts[0]?.account_key ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [tooLarge, setTooLarge] = useState(false)

  const reading = outcome?.reading ?? null
  const rec = reading?.reconciliation ?? null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Load a bank statement</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              The PDF as the bank sends it. It is read here and checked against its own opening
              balance, closing balance and printed turnover before a single line is loaded. A
              statement whose reading does not tie is not loaded at all. Once it is in, the deposit
              comparison runs by itself: exact amounts inside the clearing window are recorded, and
              anything resting on the name typed at the machine is only suggested.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <label className="block text-xs">
            <span className="font-medium text-foreground">Fleetly account</span>
            <NativeSelect
              className="mt-1 w-full"
              value={accountKey}
              disabled={busy}
              onChange={(e) => setAccountKey(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.account_key} value={a.account_key}>
                  {a.display_name}
                </option>
              ))}
            </NativeSelect>
            <span className="mt-1 block text-muted-foreground">
              The account number printed on the statement is compared against this one after it
              loads, and a disagreement is reported rather than ignored.
            </span>
          </label>

          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-6 text-center">
            <FileText className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {file ? file.name : 'Choose the statement PDF from online banking.'}
            </p>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const next = e.target.files?.[0] ?? null
                setFile(next)
                setTooLarge(next != null && next.size > MAX_BYTES)
              }}
            />
            <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
              Choose a file
            </Button>
            <p className="text-[11px] text-muted-foreground">
              A scan or a photograph of a statement cannot be reconciled and will be refused. Use
              the file downloaded from online banking.
            </p>
          </div>

          {tooLarge && (
            <p className="text-xs text-destructive">
              This file is larger than 15 MB. A statement PDF is normally well under a megabyte, so
              check that this is the statement and not a scan of one.
            </p>
          )}

          {rec && (
            <div
              className={
                rec.reconciled
                  ? 'space-y-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950'
                  : 'space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3'
              }
            >
              <div className="text-xs font-semibold text-foreground">
                {rec.reconciled
                  ? 'The reading ties to the document'
                  : 'The reading does not tie to the document'}
              </div>
              <Check
                ok={rec.balance_chain}
                label="Running balance"
                detail={`Opening ${zar(reading?.opening_balance_zar)}, and every line's balance followed to ${zar(
                  rec.closing_from_chain_zar,
                )} against a stated closing of ${zar(reading?.closing_balance_zar)}.`}
              />
              <Check
                ok={rec.credit_turnover}
                label="Credits"
                detail={`Read ${rec.read_credit_count} totalling ${zar(rec.read_credit_zar)}. The statement states ${
                  reading?.stated_credit_count ?? 'no'
                } totalling ${zar(reading?.stated_credit_zar)}.`}
              />
              <Check
                ok={rec.debit_turnover}
                label="Debits"
                detail={`Read ${rec.read_debit_count} totalling ${zar(rec.read_debit_zar)}. The statement states ${
                  reading?.stated_debit_count ?? 'no'
                } totalling ${zar(reading?.stated_debit_zar)}.`}
              />
              {reading?.unread && reading.unread.length > 0 && (
                <Check
                  ok={false}
                  label="Lines that could not be read"
                  detail={`${reading.unread.length} on the page. Until every line reads, nothing on this statement is trustworthy.`}
                />
              )}
              <p className="text-[11px] text-muted-foreground">{rec.note}</p>
            </div>
          )}

          {outcome?.account_warning && (
            <p className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {outcome.account_warning}
            </p>
          )}

          {outcome?.loaded && (
            <div className="space-y-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950">
              <p className="flex items-start gap-2 text-xs text-emerald-900 dark:text-emerald-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                Loaded. {String(outcome.result?.lines_new ?? 0)} new lines,{' '}
                {String(outcome.result?.lines_duplicate ?? 0)} already held from an overlapping
                statement and ignored.
              </p>
              {outcome.comparison && (
                <div className="border-t border-emerald-300/50 pt-2 dark:border-emerald-800/50">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                    <span className="font-medium text-foreground">Deposits compared</span>
                    <span className="tabular-nums text-emerald-900 dark:text-emerald-200">
                      {outcome.comparison.matched_total} matched and recorded
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {outcome.comparison.proposals_waiting} suggested, waiting on a person
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {outcome.comparison.note}
                  </p>
                  {outcome.comparison.credits_note && (
                    <div className="mt-2 border-t border-emerald-300/50 pt-2 dark:border-emerald-800/50">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                        <span className="font-medium text-foreground">
                          Deposits carrying a driver&rsquo;s name
                        </span>
                        <span className="tabular-nums text-emerald-900 dark:text-emerald-200">
                          {outcome.comparison.credits_attributed} recorded as his
                        </span>
                        <span className="tabular-nums text-muted-foreground">
                          {outcome.comparison.credits_left_ambiguous +
                            outcome.comparison.credits_left_unnamed}{' '}
                          left for a person
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {outcome.comparison.credits_note}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(error || outcome?.error) && (
            <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error || outcome?.error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Close
          </Button>
          <Button
            type="button"
            disabled={!file || tooLarge || busy || !accountKey}
            onClick={async () => {
              if (!file) return
              const contentBase64 = await readAsBase64(file)
              onSubmit({ accountKey, file, contentBase64 })
            }}
          >
            {busy ? <Spinner className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
            Read and load
          </Button>
        </div>
      </div>
    </div>
  )
}
